import React, { useEffect, useState } from "react";
import Input from "./Input";
import Checkbox from "./Checkbox";
import InputSelect from "./InputSelect";
import { cities } from "data.js";

const VERTICALS = {
  "Avito Market": "7100",
  "Avito Véhicules": "2000",
  "Avito Immobilier": "1000",
  "Avito Entreprise": "6000",
};

const withAdditionalPages = (arr = []) => {
  const newArr = [...arr];

  // eslint-disable-next-line no-unused-expressions
  newArr?.unshift(
    {
      value: "home",
      label: "Page d'accueil",
    },
    {
      value: "autoneuf",
      label: "Auto Neuf",
    },
  );

  return newArr;
};

const normalizeCategories = (data, excludedValues = []) => {
  if (!Array.isArray(data)) return [];

  const categoriesTree = data.slice(1);

  const safeExcludedValues = Array.isArray(excludedValues)
    ? excludedValues
    : [];

  return categoriesTree
    .map((item) => {
      const filteredChildren = item.children
        ?.filter((child) => !safeExcludedValues.includes(child.category.id))
        ?.map((child) => ({
          value: child.category.id,
          label: child.name,
        }));

      // Skip this entire category group if all its children are filtered out
      if (filteredChildren.length === 0) return null;

      return {
        label: item.name,
        options: [
          {
            value: item.name,
            label: "Tout sélectionner",
            isSelectAll: true,
            id: item.category.id,
          },
          ...filteredChildren,
        ],
      };
    })
    .filter(Boolean);
};

export default ({
  data,
  onChange,
  hideable = true,
  excludedCategoryValues = [],
}) => {
  const [show, setShow] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetch("/api/category-tree?lang=fr", {
      method: "GET",
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        return response.json();
      })
      .then((data) =>
        setCategories(normalizeCategories(data, excludedCategoryValues)),
      )
      .catch((error) => {
        console.error(
          "Encountered an error while getting listing category tree",
          {
            error: error?.message || error,
          },
        );
      });
  }, []);

  const removeSelectAllOption = (categoryLabel) => {
    setCategories((prevCategories) =>
      prevCategories.map((category) => {
        if (category.label === categoryLabel) {
          return {
            ...category,
            options: category.options.filter((option) => !option.isSelectAll),
          };
        }
        return category;
      }),
    );
  };

  const addSelectAllOption = (categoryLabel) => {
    setCategories((prevCategories) =>
      prevCategories.map((category) => {
        if (category.label === categoryLabel) {
          return {
            ...category,
            options: [
              {
                value: categoryLabel,
                label: "Select All",
                isSelectAll: true,
              },
              ...category.options.filter((option) => !option.isSelectAll),
            ],
          };
        }
        return category;
      }),
    );
  };

  const handleChange = (property, value) => {
    if (property !== "categories" || !Array.isArray(value)) {
      onChange({ ...data, [property]: value });
      return;
    }

    let processedSelections = expandSelectAllOptions(value);
    processedSelections =
      removeDuplicatesAndInvalidVerticals(processedSelections);

    updateSelectAllUI(processedSelections);

    onChange({ ...data, [property]: processedSelections });
  };

  // Expand Select All options and handle vertical items
  const expandSelectAllOptions = (selectedOptions) => {
    let expanded = selectedOptions.flatMap((option) => {
      if (option.isSelectAll) {
        const category = categories.find((cat) => cat.label === option.value);
        if (category) {
          return category.options.filter((opt) => !opt.isSelectAll);
        }
      }
      return option;
    });

    categories.forEach((category) => {
      const childOptions = category.options.filter((opt) => !opt.isSelectAll);
      const allChildrenSelected = childOptions.every((childOpt) =>
        expanded.some((sel) => sel.value === childOpt.value),
      );

      if (allChildrenSelected) {
        const verticalId = VERTICALS[category.label] || category.options[0]?.id;
        const verticalItem = {
          value: verticalId,
          label: category.label,
        };

        if (!expanded.some((item) => item.value === verticalItem.value)) {
          expanded.push(verticalItem);
        }
      }
    });

    return expanded;
  };

  // Clean up selections by removing duplicates and validating verticals
  const removeDuplicatesAndInvalidVerticals = (selections) => {
    const unique = Array.from(
      new Set(selections.map((item) => item.value)),
    ).map((value) => selections.find((item) => item.value === value));

    return unique.filter((item) => {
      if (!isVerticalItem(item)) {
        return true;
      }

      const category = categories.find((cat) => cat.label === item.label);
      if (!category) return false;

      return category.options
        .filter((opt) => !opt.isSelectAll)
        .every((opt) => unique.some((sel) => sel.value === opt.value));
    });
  };

  const isVerticalItem = (item) => {
    const verticalValues = Object.values(VERTICALS);
    return verticalValues.includes(item.value);
  };

  const updateSelectAllUI = (finalSelections) => {
    categories.forEach((category) => {
      const childOptions = category.options.filter((opt) => !opt.isSelectAll);
      const allChildrenSelected = childOptions.every((childOpt) =>
        finalSelections.some((sel) => sel.value === childOpt.value),
      );

      if (allChildrenSelected) {
        removeSelectAllOption(category.label);
      } else {
        const categoryId = category.options[0]?.id;
        addSelectAllOption(category.label, categoryId);
      }
    });
  };

  return (
    <>
      {hideable ? (
        <Checkbox
          options={[{ value: "show", label: "Activer le targeting avancé" }]}
          value={show}
          onChange={(val) => {
            setShow(val);
            if (val.length === 0) onChange({});
          }}
          style={{ marginBottom: 36 }}
        />
      ) : (
        <div style={{ marginBottom: 36, fontWeight: 600 }}>
          Targeting avancé
        </div>
      )}
      {(!hideable || show.length > 0) && (
        <>
          <InputSelect
            label="Categories"
            options={withAdditionalPages(categories)}
            multiple
            clearable={false}
            onChange={(categories) => handleChange("categories", categories)}
            value={data.categories || []}
            placeholder="Toutes les categories"
            style={{ marginBottom: 36 }}
          />
          <InputSelect
            label="Villes"
            options={cities}
            clearable={false}
            multiple
            onChange={(cities) => handleChange("cities", cities)}
            value={data.cities || []}
            placeholder="Toutes les villes"
            style={{ marginBottom: 36 }}
          />
          <div style={{ display: "flex" }}>
            <Input
              value={data.priceMin || ""}
              onChange={(e) => handleChange("priceMin", e.target.value)}
              style={{
                marginRight: 20,
                opacity: data.priceMin?.length > 0 ? 1 : 0.5,
              }}
              type="number"
              label="Prix min"
            />
            <Input
              value={data.priceMax || ""}
              onChange={(e) => handleChange("priceMax", e.target.value)}
              style={{
                marginLeft: 20,
                opacity: data.priceMax?.length > 0 ? 1 : 0.5,
              }}
              type="number"
              label="Prix max"
            />
          </div>
        </>
      )}
    </>
  );
};
